import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import RecipeCard from '../components/recipes/RecipeCard';
import { Heart, ChefHat, BookOpen, Search } from 'lucide-react';
import { Button, EmptyState } from '../components/common';

export default function MyCookbookPage() {
  const { recipes } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('saved');
  const [searchQuery, setSearchQuery] = useState('');

  const savedRecipes = useMemo(() => recipes.filter((r) => r.favorite), [recipes]);
  const cookedRecipes = useMemo(
    () => recipes
      .filter((r) => (r.cookCount || 0) > 0)
      .sort((a, b) => new Date(b.lastCookedAt || 0) - new Date(a.lastCookedAt || 0)),
    [recipes]
  );

  const list = activeTab === 'saved' ? savedRecipes : cookedRecipes;
  const displayRecipes = !searchQuery
    ? list
    : list.filter((r) => {
        const q = searchQuery.toLowerCase();
        return r.title?.toLowerCase().includes(q)
          || r.cuisine?.toLowerCase().includes(q)
          || r.tags?.some((t) => t.toLowerCase().includes(q));
      });

  const tabClass = (active) =>
    [
      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
      active
        ? 'bg-white dark:bg-night-card text-gray-900 dark:text-night-text shadow-sm'
        : 'text-gray-600 dark:text-night-muted',
    ].join(' ');

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-night-text">My Cookbook</h2>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search your cookbook…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-night-surface border border-gray-200 dark:border-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div role="tablist" className="inline-flex p-1 rounded-lg bg-gray-100 dark:bg-night-surface">
        <button role="tab" aria-selected={activeTab === 'saved'} onClick={() => setActiveTab('saved')} className={tabClass(activeTab === 'saved')}>
          <Heart size={16} aria-hidden="true" /> Saved ({savedRecipes.length})
        </button>
        <button role="tab" aria-selected={activeTab === 'cooked'} onClick={() => setActiveTab('cooked')} className={tabClass(activeTab === 'cooked')}>
          <ChefHat size={16} aria-hidden="true" /> Cooked ({cookedRecipes.length})
        </button>
      </div>

      {displayRecipes.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {displayRecipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={
            activeTab === 'saved'
              ? <BookOpen size={48} className="text-gray-400 dark:text-night-muted" aria-hidden="true" />
              : <ChefHat size={48} className="text-gray-400 dark:text-night-muted" aria-hidden="true" />
          }
          title={activeTab === 'saved' ? 'Your cookbook is empty' : 'No cooking history yet'}
          description={
            activeTab === 'saved'
              ? 'Browse recipes and tap the heart to save your favourites here.'
              : 'Start cooking and your history will appear here.'
          }
          action={
            <Button onClick={() => navigate('/')}>
              {activeTab === 'saved' ? 'Explore Recipes' : 'Find Something to Cook'}
            </Button>
          }
        />
      )}
    </div>
  );
}
